import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "@/lib/apiFetch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type Analysis = {
  id: number; parent_id: number | null; project_id: number; prompt: string; status: string;
  stage: string; total_documents: number; ready_documents: number; failed_documents: number;
  total_sections: number; completed_sections: number; failed_sections: number;
  report?: string; error_message?: string; created_at: string; input_tokens?: number; output_tokens?: number;
};
type Page<T> = { data: T[]; current_page: number; last_page: number; total: number };
type Coverage = { id: number; source_path: string; status: string; warnings: string | string[] | null; error_message: string | null; section_count: number };
type Evidence = { id: number; statement: string; quote: string; source_path: string; location: string };
type Source = { quote: string; content: string; source_path: string; location: string; content_hash: string };
type Failure = { work_key: string; stage: string; error_message: string };
const active = (status: string) => status === "queued" || status === "processing";

async function mutate<T>(path: string, body: Record<string, unknown> = {}): Promise<T> {
  await fetch("/sanctum/csrf-cookie", { credentials: "include" });
  return apiFetch<T>(path, { method: "POST", body });
}

export default function ProjectAnalyses() {
  const [params, setParams] = useSearchParams();
  const selected = Number(params.get("analysis")) || null;
  const [page, setPage] = useState(1);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [listing, setListing] = useState<Page<Analysis> | null>(null);
  const [run, setRun] = useState<Analysis | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [coverage, setCoverage] = useState<Page<Coverage> | null>(null);
  const [evidence, setEvidence] = useState<Page<Evidence> | null>(null);
  const [failures, setFailures] = useState<Page<Failure> | null>(null);
  const [source, setSource] = useState<Source | null>(null);
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [detail, setDetail] = useState<"coverage" | "evidence" | "failures" | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const detailRequest = useRef(0);
  const refinementKey = useRef<{ parent: number; prompt: string; key: string } | null>(null);

  const select = (id: number | null) => {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (id) next.set("analysis", String(id)); else next.delete("analysis");
      return next;
    });
  };

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const result = await apiFetch<Page<Analysis>>(`/api/project-analyses?page=${page}`);
        if (!stopped) { setListing(result); setError(""); }
      } catch (e) {
        if (!stopped) setError(e instanceof Error ? e.message : "Could not load project analyses");
      } finally {
        if (!stopped) timer = setTimeout(refresh, 10000);
      }
    };
    refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [page]);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    setRun(null); setPrompt(""); setSource(null); setDetail(null);
    setCoverage(null); setEvidence(null); setFailures(null);
    detailRequest.current++;
    if (!selected) return;
    const refresh = async () => {
      try {
        const result = await apiFetch<Analysis>(`/api/project-analyses/${selected}`);
        if (!stopped) {
          setRun(result);
          if (active(result.status)) timer = setTimeout(refresh, 5000);
        }
      } catch (e) {
        if (!stopped) setError(e instanceof Error ? e.message : "Could not load report");
      }
    };
    refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [selected, refreshVersion]);

  const loadDetail = useCallback(async (kind: "coverage" | "evidence" | "failures", number = 1) => {
    if (!selected) return;
    const request = ++detailRequest.current;
    setDetail(kind); setDetailBusy(true);
    try {
      const result = await apiFetch<Page<Coverage> | Page<Evidence> | Page<Failure>>(`/api/project-analyses/${selected}/${kind}?page=${number}`);
      if (request !== detailRequest.current) return;
      if (kind === "coverage") setCoverage(result as Page<Coverage>);
      if (kind === "evidence") setEvidence(result as Page<Evidence>);
      if (kind === "failures") setFailures(result as Page<Failure>);
    } catch (e) {
      if (request === detailRequest.current) setError(e instanceof Error ? e.message : "Could not load details");
    } finally { if (request === detailRequest.current) setDetailBusy(false); }
  }, [selected]);

  async function openSource(id: number) {
    const request = ++detailRequest.current;
    setSource(null); setSourceId(null); setDetailBusy(true);
    try {
      const result = await apiFetch<Source>(`/api/project-analyses/${selected}/evidence/${id}`);
      if (request === detailRequest.current) { setSource(result); setSourceId(id); }
    } catch (e) { setError(e instanceof Error ? e.message : "Could not load source"); }
    finally { if (request === detailRequest.current) setDetailBusy(false); }
  }

  async function refine() {
    if (!selected || !prompt.trim()) return;
    setBusy(true); setError("");
    const text = prompt.trim();
    if (refinementKey.current?.parent !== selected || refinementKey.current?.prompt !== text) {
      refinementKey.current = { parent: selected, prompt: text, key: crypto.randomUUID() };
    }
    try {
      const result = await mutate<{ id: number }>(`/api/project-analyses/${selected}/refine`, {
        prompt: text, request_key: refinementKey.current.key,
      });
      refinementKey.current = null;
      select(result.id); setPage(1);
      setListing(await apiFetch<Page<Analysis>>("/api/project-analyses?page=1"));
    } catch (e) { setError(e instanceof Error ? e.message : "Could not queue refinement"); }
    finally { setBusy(false); }
  }

  async function control(action: "cancel" | "retry") {
    if (!selected) return;
    setBusy(true); setError("");
    try {
      await mutate(`/api/project-analyses/${selected}/${action}`);
      setRun(await apiFetch<Analysis>(`/api/project-analyses/${selected}`));
      setListing(await apiFetch<Page<Analysis>>(`/api/project-analyses?page=${page}`));
      if (action === "retry") setRefreshVersion(v => v + 1);
    } catch (e) { setError(e instanceof Error ? e.message : "Action failed"); }
    finally { setBusy(false); }
  }

  function downloadReport() {
    if (!run?.report) return;
    const url = URL.createObjectURL(new Blob([run.report], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a"); link.href = url; link.download = `project-analysis-${run.id}.txt`;
    link.click(); URL.revokeObjectURL(url);
  }

  const reportText = (text: string) => text.split(/(\[E\d+\])/g).map((part, i) => {
    const match = /^\[E(\d+)\]$/.exec(part);
    return match ? <button key={i} className="text-primary underline" onClick={() => openSource(Number(match[1]))}>{part}</button> : part;
  });
  const detailPage = detail === "coverage" ? coverage : detail === "evidence" ? evidence : failures;

  return <section className="space-y-3 border rounded-lg p-4">
    <h2 className="text-xl font-semibold">Full-project analyses</h2>
    <p className="text-sm text-muted-foreground">Review every document, inspect evidence, and refine completed reports with new prompts.</p>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {!listing && !error && <p>Loading analyses…</p>}
    {listing?.data.length === 0 && <p className="text-sm text-muted-foreground">No full-project analyses yet. Start one from General AI Queries.</p>}
    {listing?.data.map(item => <div key={item.id} className="border-t py-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <button className="text-primary text-left underline break-words" onClick={() => select(item.id)}>{item.prompt}</button>
        <p className="text-xs text-muted-foreground">Report #{item.id}{item.parent_id ? ` · Refines #${item.parent_id}` : ""} · {item.created_at}</p>
        <p className="text-sm">{item.completed_sections}/{item.total_sections} sections reviewed · {item.ready_documents}/{item.total_documents} documents extracted</p>
      </div>
      <span className="text-sm capitalize shrink-0">{item.status} · {item.stage}</span>
    </div>)}
    {listing && listing.last_page > 1 && <div className="flex items-center gap-3">
      <Button variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
      <span>Page {listing.current_page} of {listing.last_page}</span>
      <Button variant="outline" disabled={page >= listing.last_page} onClick={() => setPage(p => p + 1)}>Next</Button>
    </div>}
    <Dialog open={selected !== null} onOpenChange={open => { if (!open) select(null); }}>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Full-project report #{selected}</DialogTitle>
          <DialogDescription>{run?.prompt || "Loading report…"}</DialogDescription></DialogHeader>
        {error && <p role="alert" className="text-red-600">{error}</p>}
        {run && <>
          <p className="capitalize">{run.status} · {run.stage}</p>
          {run.parent_id && <button className="text-primary underline text-left" onClick={() => select(run.parent_id)}>View previous report #{run.parent_id}</button>}
          <p>{run.ready_documents}/{run.total_documents} documents extracted · {run.completed_sections}/{run.total_sections} sections reviewed</p>
          {active(run.status) && <p className="text-sm text-muted-foreground">Processing continues if you leave this page. Progress refreshes automatically.</p>}
          {run.error_message && <p className="text-red-600">{run.error_message}</p>}
          {run.status === "incomplete" && <p className="text-amber-700">This report has source coverage gaps or claims requiring review. Inspect coverage and verification notes. Start a new project analysis after repairing source extraction gaps.</p>}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => loadDetail("coverage")}>Source coverage</Button>
            <Button variant="outline" onClick={() => loadDetail("evidence")}>Evidence</Button>
            <Button variant="outline" onClick={() => loadDetail("failures")}>Processing failures</Button>
            {active(run.status) && <Button variant="outline" disabled={busy} onClick={() => control("cancel")}>Cancel analysis</Button>}
            {["failed", "cancelled"].includes(run.status) && <Button disabled={busy} onClick={() => control("retry")}>Resume analysis</Button>}
            {run.report && <Button variant="outline" onClick={downloadReport}>Download report</Button>}
          </div>
          {detailBusy && <p>Loading evidence…</p>}
          {source && <div className="border p-3 space-y-2">
            <div className="flex justify-between"><strong>{source.source_path} · {source.location}</strong><Button variant="ghost" onClick={() => setSource(null)}>Close source</Button></div>
            <blockquote className="border-l-4 pl-3 whitespace-pre-wrap">{source.quote}</blockquote>
            <details><summary>Original saved section</summary><p className="whitespace-pre-wrap break-words max-h-64 overflow-auto">{source.content}</p></details>
            <p className="text-xs break-all">Reviewed file SHA-256: {source.content_hash}</p>
            <a className="text-primary underline" href={`/api/project-analyses/${selected}/evidence/${sourceId}?download=1`}>Download reviewed original</a>
          </div>}
          {detail && detailPage && <div className="border p-3 space-y-2">
            <div className="flex justify-between"><h3 className="font-semibold capitalize">{detail}</h3><Button variant="ghost" onClick={() => setDetail(null)}>Close details</Button></div>
            <div className="max-h-64 overflow-auto space-y-3">
              {detail === "coverage" && coverage?.data.map(d => <div key={d.id} className="text-sm border-b pb-2">
                <p className="break-all">{d.source_path}</p><p>{d.status} · {d.section_count} sections</p>
                {d.error_message && <p className="text-red-600">{d.error_message}</p>}
                {d.warnings && <p className="text-amber-700">{typeof d.warnings === "string" ? d.warnings : d.warnings.join("; ")}</p>}
              </div>)}
              {detail === "evidence" && evidence?.data.map(e => <div key={e.id} className="text-sm border-b pb-2">
                <button className="text-primary underline" onClick={() => openSource(e.id)}>[E{e.id}] {e.source_path} · {e.location}</button><p>{e.statement}</p>
              </div>)}
              {detail === "failures" && failures?.data.map(f => <p key={f.work_key} className="text-sm">{f.work_key}: {f.error_message}</p>)}
              {detailPage.total === 0 && <p>No records.</p>}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={detailBusy || detailPage.current_page <= 1} onClick={() => loadDetail(detail, detailPage.current_page - 1)}>Previous</Button>
              <span>{detailPage.current_page} / {detailPage.last_page}</span>
              <Button variant="outline" disabled={detailBusy || detailPage.current_page >= detailPage.last_page} onClick={() => loadDetail(detail, detailPage.current_page + 1)}>Next</Button>
            </div>
          </div>}
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{run.report ? reportText(run.report) : "The report will appear here after analysis and verification finish."}</div>
          {["completed", "incomplete"].includes(run.status) && <div className="border-t pt-4 space-y-3">
            <h3 className="font-semibold">Refine this report</h3>
            <p className="text-sm text-muted-foreground">A new report version will review every saved text section using your original request and follow-up prompts. The previous report is retained. Documents added later require a new project analysis.</p>
            <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} maxLength={6000} placeholder="For example: Focus on the timeline and clearly distinguish disputed events from corroborated accounts." />
            <Button disabled={busy || !prompt.trim()} onClick={refine}>{busy ? "Queuing…" : "Create refined report"}</Button>
          </div>}
        </>}
      </DialogContent>
    </Dialog>
  </section>;
}
